import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { TabFilter } from "@/components/TabFilter";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { confirmAction } from "@/lib/dialogs";
import { CompanyField } from "@/components/CompanyField";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const EMPTY = {
  company_id: "", code: "", name: "", designation: "", phone: "", email: "", pan: "", uan: "",
  bank_name: "", bank_account_no: "", ifsc: "", project_id: "", joining_date: "",
  monthly_gross: "", basic_pct: "50", pf_applicable: true, pf_on_full_basic: false, esi_applicable: false,
  monthly_tds: "0", professional_tax: "0", is_active: true,
};

export default function Employees() {
  const { can } = usePermissions();
  const canCreate = can("EMPLOYEES", "create");
  const canEdit = can("EMPLOYEES", "edit");
  const canDelete = can("EMPLOYEES", "delete");
  const { companies } = useCompany() || {};
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [logins, setLogins] = useState({});                 // employee id -> the login that clocks in as them
  const [linking, setLinking] = useState(null);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY); });      // closed without saving: opens fresh next time
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  // Only for someone entered by mistake: the server refuses anyone already on a payroll run (they are made inactive instead).
  const removeEmployee = async (e) => {
    const ok = await confirmAction({ title: `Delete ${e.name} (${e.code})?`, message: "Their attendance and leave records are deleted too. Someone already on a payroll run cannot be deleted; mark them inactive instead.", confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api.delete(`/payroll/employees/${e.id}`);
      toast.success(`${e.name} deleted`);
      if (selected?.id === e.id) setSelected(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, pr, lg] = await Promise.all([api.get("/payroll/employees"), api.get("/projects").catch(() => ({ data: [] })), api.get("/hr/employee-logins").catch(() => ({ data: {} }))]);
      setEmployees(data);
      setProjects(pr.data || []);
      setLogins(lg?.data || {});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, company_id: companies?.[0]?.id || "" }); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, e[k] === null || e[k] === undefined ? "" : typeof EMPTY[k] === "boolean" ? e[k] : String(e[k])])));
    setOpen(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, project_id: form.project_id || null, joining_date: form.joining_date || null };
      for (const k of ["designation", "phone", "email", "pan", "uan", "bank_name", "bank_account_no", "ifsc"]) payload[k] = payload[k] || null;
      if (editing) await api.patch(`/payroll/employees/${editing.id}`, payload);
      else await api.post("/payroll/employees", payload);
      toast.success(editing ? "Employee updated" : "Employee added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.code;
  const monthlyCost = employees.filter((e) => e.is_active).reduce((s, e) => s + Number(e.monthly_gross), 0);

  const tabs = [
    { key: "ALL",      label: "All Employees", count: employees.length , sub: inr(monthlyCost) + " / month" },
    { key: "ACTIVE",   label: "Active",         count: employees.filter(e => e.is_active).length },
    { key: "INACTIVE", label: "Inactive",        count: employees.filter(e => !e.is_active).length },
    { key: "SITE",     label: "Site Staff",      count: employees.filter(e => e.project_id).length },
    { key: "OFFICE",   label: "Office",          count: employees.filter(e => !e.project_id).length },
  ];

  const shown = useMemo(() => {
    let list = employees;
    if (activeTab === "ACTIVE")   list = list.filter(e => e.is_active);
    else if (activeTab === "INACTIVE") list = list.filter(e => !e.is_active);
    else if (activeTab === "SITE")     list = list.filter(e => e.project_id);
    else if (activeTab === "OFFICE")   list = list.filter(e => !e.project_id);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.code || "").toLowerCase().includes(q) ||
        (e.name || "").toLowerCase().includes(q) ||
        (e.designation || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [employees, activeTab, search]);

  return (
    <div className="space-y-6" data-testid="employees-page">
      <PageHeader
        title="Employees"
        description="Salary details used by the monthly payroll run. Site staff linked to a project have their salary booked as that project's labour cost."
        action={canCreate && <Button onClick={openNew} data-testid="add-employee-button"><Plus className="h-4 w-4" /> Add Employee</Button>}
      />
      <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map(e => ({
          Code: e.code, Name: e.name, Designation: e.designation || "",
          "Monthly Gross": e.monthly_gross, PF: e.pf_applicable ? "Yes" : "No",
          ESI: e.esi_applicable ? "Yes" : "No", Status: e.is_active ? "Active" : "Inactive",
        }))}
        csvFilename="employees.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && employees.length === 0 ? (
          <EmptyState title="No employees yet" description="Add your office and site staff to run payroll." testId="employees-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Designation</TableHead><TableHead>Works at</TableHead>
              <TableHead className="text-right">Monthly gross</TableHead><TableHead>PF / ESI</TableHead><TableHead>Bank</TableHead><TableHead>Login</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.code}`}>
                  <TableCell className="font-mono text-xs">{e.code}</TableCell>
                  <TableCell className="text-foreground">{e.name}</TableCell>
                  <TableCell className="text-muted-foreground">{e.designation || "—"}</TableCell>
                  <TableCell className="text-xs">{e.project_id ? projectName(e.project_id) || "Project" : <span className="text-muted-foreground">Office</span>}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{inr(e.monthly_gross)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.pf_applicable ? "PF" : "—"}{e.esi_applicable ? " · ESI" : ""}</TableCell>
                  <TableCell className="text-xs">{e.bank_account_no && e.ifsc ? <span className="text-emerald-600 dark:text-emerald-400">Added</span> : <span className="text-amber-600 dark:text-amber-400">Missing</span>}</TableCell>
                  <TableCell className="text-xs" data-testid={`employee-login-${e.code}`}>
                    {logins[e.id] ? <span>{logins[e.id].name || "Linked"}</span> : <span className="text-muted-foreground">None</span>}
                    {canEdit && <button type="button" className="ml-2 text-primary underline" onClick={() => setLinking(e)} data-testid={`employee-link-${e.code}`}>{logins[e.id] ? "Change" : "Link"}</button>}
                  </TableCell>
                  <TableCell className="text-xs">{e.is_active ? "Active" : <span className="text-muted-foreground">Inactive</span>}</TableCell>
                  <TableCell>
                    <RowActions
                      testId={`employee-actions-${e.code}`}
                      onView={() => setSelected(e)}
                      onEdit={canEdit ? () => openEdit(e) : undefined}
                      moreItems={canDelete ? [{ label: `Delete ${e.name}`, icon: Trash2, danger: true, onClick: () => removeEmployee(e), testId: `employee-delete-${e.code}` }] : []}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`Employee · ${selected?.code}`}
      >
        <DrawerSection label="Basic Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="Designation" value={selected?.designation} />
          <DrawerField label="Joining Date" value={selected?.joining_date} />
          <DrawerField label="Status" value={selected?.is_active ? "Active" : "Inactive"} />
          <DrawerField label="Works At" value={selected?.project_id ? (projectName(selected.project_id) || "Site Project") : "Office"} />
        </DrawerSection>
        <DrawerSection label="Salary & Statutory">
          <DrawerField label="Monthly Gross" value={selected?.monthly_gross ? inr(selected.monthly_gross) : null} mono />
          <DrawerField label="Basic %" value={selected?.basic_pct ? `${selected.basic_pct}%` : null} />
          <DrawerField label="PF Applicable" value={selected?.pf_applicable ? "Yes" : "No"} />
          <DrawerField label="ESI Applicable" value={selected?.esi_applicable ? "Yes" : "No"} />
          <DrawerField label="Monthly TDS" value={selected?.monthly_tds ? inr(selected.monthly_tds) : null} mono />
        </DrawerSection>
        <DrawerSection label="Contact & Bank">
          <DrawerField label="Phone" value={selected?.phone} />
          <DrawerField label="Email" value={selected?.email} />
          <DrawerField label="PAN" value={selected?.pan} mono />
          <DrawerField label="UAN" value={selected?.uan} mono />
          <DrawerField label="Bank Account" value={selected?.bank_account_no} mono />
          <DrawerField label="IFSC" value={selected?.ifsc} mono />
        </DrawerSection>
      </DetailDrawer>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "Add Employee"}</DialogTitle>
            <DialogDescription>Statutory defaults: PF 12% of basic (capped at ₹15,000 unless "PF on full basic"), ESI 0.75% / 3.25% of gross. Confirm rates with your CA.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="employee-form">
            <div className="grid gap-3 sm:grid-cols-3">
              <div><Label>Code</Label><Input placeholder="Automatic if blank" value={form.code} onChange={set("code")} className="mt-1.5" /></div>
              <div className="sm:col-span-2"><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" /></div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} className="mt-1.5" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
              <div><Label>Joining date</Label><Input type="date" value={form.joining_date} onChange={set("joining_date")} className="mt-1.5" /></div>
              <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} />
              <div className="sm:col-span-2">
                <Label>Works at <span className="font-normal text-muted-foreground">(project = salary counts as that site's labour cost)</span></Label>
                <Select value={form.project_id || "office"} onValueChange={(v) => setForm({ ...form, project_id: v === "office" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">Office (company overhead)</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-sm font-medium text-foreground">Salary</p>
              <div className="grid gap-3 sm:grid-cols-4">
                <div><Label>Monthly gross (₹)</Label><Input required type="number" step="0.01" min="0" value={form.monthly_gross} onChange={set("monthly_gross")} className="mt-1.5" data-testid="employee-gross-input" /></div>
                <div><Label>Basic % of gross</Label><Input type="number" step="0.01" min="0" max="100" value={form.basic_pct} onChange={set("basic_pct")} className="mt-1.5" /></div>
                <div><Label>TDS per month (₹)</Label><Input type="number" step="0.01" min="0" value={form.monthly_tds} onChange={set("monthly_tds")} className="mt-1.5" /></div>
                <div><Label>Professional tax (₹)</Label><Input type="number" step="0.01" min="0" value={form.professional_tax} onChange={set("professional_tax")} className="mt-1.5" /></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-5 text-sm">
                <label className="flex items-center gap-2"><Switch checked={form.pf_applicable} onCheckedChange={(v) => setForm({ ...form, pf_applicable: v })} /> PF</label>
                <label className="flex items-center gap-2"><Switch checked={form.pf_on_full_basic} onCheckedChange={(v) => setForm({ ...form, pf_on_full_basic: v })} disabled={!form.pf_applicable} /> PF on full basic</label>
                <label className="flex items-center gap-2"><Switch checked={form.esi_applicable} onCheckedChange={(v) => setForm({ ...form, esi_applicable: v })} /> ESI</label>
                <label className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> Active</label>
              </div>
            </div>

            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-sm font-medium text-foreground">Statutory IDs &amp; bank (for payslips and the salary transfer file)</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><Label>PAN</Label><Input value={form.pan} onChange={set("pan")} className="mt-1.5" /></div>
                <div><Label>UAN (PF)</Label><Input value={form.uan} onChange={set("uan")} className="mt-1.5" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
                <div><Label>Bank</Label><Input value={form.bank_name} onChange={set("bank_name")} className="mt-1.5" /></div>
                <div><Label>Account number</Label><Input value={form.bank_account_no} onChange={set("bank_account_no")} className="mt-1.5" /></div>
                <div><Label>IFSC</Label><Input value={form.ifsc} onChange={set("ifsc")} className="mt-1.5" /></div>
              </div>
            </div>
            <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {linking && <LinkLoginDialog employee={linking} current={logins[linking.id]} onClose={() => setLinking(null)} onDone={() => { setLinking(null); load(); }} />}
    </div>
  );
}


/** Tie a BuildNexa login to an employee, so the person can clock themselves in and out. */
function LinkLoginDialog({ employee, current, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(current?.user_id || "none");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers((data || []).filter((u) => u.is_active))).catch(() => setUsers([])); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/hr/employees/${employee.id}/login`, { user_id: userId === "none" ? null : userId });
      toast.success(userId === "none" ? `${employee.name} has no login now` : `${employee.name} can now clock in with that login`);
      onDone();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="link-login-dialog">
        <DialogHeader><DialogTitle>Login for {employee.name}</DialogTitle>
          <DialogDescription>The person signs in with this login and clocks themselves in and out. Someone without a login can be clocked in by a supervisor.</DialogDescription></DialogHeader>
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger data-testid="link-login-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="none">No login</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}{u.designation ? ` — ${u.designation}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={save} data-testid="link-login-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
